import {
  call, put, takeLatest, fork, takeEvery, delay, take, select,
} from 'redux-saga/effects';
import md5 from 'md5';
import api from '@/api/auth';
import userApi from '@/api/user';
import utils from '@/utils/auth';

import {
  ANALYTICS_SEND_EVENT,
  ANALYTICS_SET_ID,
  ANALYTICS_SET_USER_PROPERTIES,
  GA_SEND_EVENT,
} from '@/action-types/analytics';
import {
  ERROR_PASS_REQUEST,
  ERROR_UPDATE_REQUEST,
  VALIDATION_ERROR_REQUEST,
} from '@/action-types/error';
import {
  LOADING_UPDATE_REQUEST,
} from '@/action-types/loading';
import {
  LOGIN_INIT_REQUEST,
  LOGIN_INIT_SUCCESS,
  LOGIN_AUTH_REQUEST,
  REGISTRATION_AUTH_REQUEST,
  AUTH_SUCCESS,
  LOGIN_AUTH_REFRESH_REQUEST,
  LOGIN_AUTH_REFRESH_SUCCESS,
  LOGIN_RESET_STATE_REQUEST,
  LOGIN_RESET_STATE_SUCCESS,
  LOGIN_LOGOUT_REQUEST,
  LOGIN_AUTH_UPDATE_ACCESS,
  REGISTRATION_AUTH_SUCCESS,

  REGISTRATION_TEACHER_PAY_REQUEST,
  REGISTRATION_STUDENT_PAY_REQUEST,
  REGISTRATION_PAY_SUCCESS,
  LOGIN_RESTORE_REQUEST,

  AUTH_AS_USER_REQUEST,
} from '@/action-types/login';
import {
  USER_ADD_DATA_SUCCESS,
  USER_ADD_GOOGLE_DATA_SUCCESS,
  USER_REGISTRATION_PAY_REQUEST,
  USER_REGISTRATION_REQUEST,
} from '@/action-types/user';

import { decodeInviteLink } from '@/utils/deencode-invite-link';
import { Action } from 'redux';
import { REDIRECT_ACTION } from '@/action-types/redirect';
import { STUDENT_GET_INFO_REQUEST } from '@/action-types/student';
import { UserRegistraionData } from './user';
// import { dashboards } from '@/services/auth';

export const dashboards : {[key: string]: string;} = {
  ROLES_TEACHER: '/teacher/',
  teacher: '/teacher/',
  ROLES_TEACHER_PAY: '/teacher/',
  ROLES_STUDENT: '/student/',
  ROLE_STUDENT_B2B: '/student/',
  student: '/student/',
  ROLES_STUDENT_PAY: '/student/',
  admin: '/qa/',
  b2bAdmin: '/add_b2bcompany/',
};

export interface Token {
  access_token: string;
  refresh_token: string;
}

const cbRefreshQueue : Function[] = [];

export function* regLoginSuccess(token: Token) {
  const userData = yield call(utils.parseToken, token.access_token);
  const queryParams = new URLSearchParams(window.location.search);

  const redirect = queryParams.get('redirect');

  if (userData.role === 'student') {
    yield put({ type: STUDENT_GET_INFO_REQUEST });
    yield take(USER_ADD_DATA_SUCCESS);
  }

  yield put({
    type: REDIRECT_ACTION,
    payload: {
      path: redirect ?? `${dashboards[userData.role]}`,
      state: {
        from: {
          pathname: window.location.pathname,
        },
      },
    },
  });
}

export function* authSuccess(params: {
  token: Token;
  loginType?: string;
  regType?: string;
  regData?: UserRegistraionData;
}) {
  const userData = yield call(utils.parseToken, params.token.access_token);

  // сохраняем токен в куку
  yield fork(utils.saveToken, params.token);
  // сетим данные в стор, если этого не сделать на этом этапе у нас не будет айди юзера
  yield put({ type: USER_ADD_DATA_SUCCESS, userData });

  // сетим айдишник в аналитику
  yield put({ type: ANALYTICS_SET_ID, id: userData.uid });

  if (!userData.role && params.regType === 'google') {
    // if registration - OPEN WINDOW
    yield put({
      type: AUTH_SUCCESS,
      isNeedFillInfo: true,
      registrationType: params.regType,
    });
  } else if (!userData.role && params.regType === 'email') {
    yield put({
      type: USER_REGISTRATION_PAY_REQUEST,
      data: params.regData,
      hasValidation: true,
    });
  } else {
    // LOGIN FLOW

    // TODO ? not need???
    yield put({
      type: AUTH_SUCCESS,
      isNeedFillInfo: false,
    });

    yield put({
      type: ANALYTICS_SET_USER_PROPERTIES,
      data: {
        role: userData.role,
      },
    });
    yield put({
      type: ANALYTICS_SEND_EVENT,
      eventName: 'login',
      data: {
        type: params.loginType,
        fromPage: window.location.pathname,
      },
    });

    yield fork(regLoginSuccess, params.token);
  }
}

export function* checkAuthErrors(e) {
  if (e.message === 'User Input Error') {
    yield put({ type: VALIDATION_ERROR_REQUEST, isActive: true, errorData: e });
  } else {
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  }
}

export function* authContinue(params: {
  username: string;
  password: string;
  loginType?: string;
  regType?: string;
  regData?: UserRegistraionData;
}) {
  try {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: true });

    const response = yield call(api.siteAuth, params);

    const tokenData = (response?.data?.tokenAccess ?? {});

    if (tokenData.access_token) {
      yield fork(authSuccess, {
        token: tokenData,
        ...params,
      });
    } else {
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'authContinue'] });
    yield checkAuthErrors(e);
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: false });
  }
}

export function* authRestoreContinue(
  username: string,
  hash: string,
) {
  try {
    const response = yield call(api.siteAuthRestore, {
      username,
      hash,
    });

    const tokenData = (response?.data?.tokenAccessRestore ?? {});

    if (tokenData.access_token) {
      yield fork(authSuccess, {
        token: tokenData,
        loginType: 'restore',
      });
    } else {
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'authRestoreContinue'] });
    yield checkAuthErrors(e);
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: false });
  }
}

export function* registration(username: string, password: string) {
  try {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: true });

    const response = yield call(api.siteRegistration, {
      username,
      password,
    });

    if (response.data.userRegistration) {
      yield put({ type: REGISTRATION_AUTH_SUCCESS });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'registration'] });
    yield checkAuthErrors(e);
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: false });
  }
}

export function* registrationPay(username: string, password: string) {
  try {
    const response = yield call(api.sitePayRegistration, {
      username,
      password,
    });

    if (response.data.userPayRegistration) {
      yield put({ type: REGISTRATION_PAY_SUCCESS });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'registrationPay'] });
    yield checkAuthErrors(e);
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: false });
  }
}

// REFRESH ACCESS TOKEN when 401
export function* authRefresh(params: Action & { cb: Function; }) {
  try {
    cbRefreshQueue.push(params.cb);

    if (cbRefreshQueue.length === 1) {
      yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: true });
      yield fork(utils.deleteToken, false);

      const response = yield call(api.siteAuthRefresh);
      const tokenData = (response?.data?.tokenRefresh ?? {});

      if (tokenData.access_token) {
        yield fork(utils.saveToken, tokenData);
        yield put({ type: LOGIN_AUTH_REFRESH_SUCCESS });
      } else {
        yield put({ type: ERROR_UPDATE_REQUEST, isActive: true });
        yield put({ type: LOGIN_LOGOUT_REQUEST });
      }
    }

    cbRefreshQueue.forEach((cb: Function) => {
      if (cb) cb();
      cbRefreshQueue.splice(0, 1);
    });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'authRefresh'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: false });
  }
}

// Update access token after registration
export function* authUpdateAccess() {
  try {
    const state = yield select();

    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: true });
    // yield fork(utils.deleteToken, false);

    const response = yield call(api.siteAuthRefresh);
    const tokenData = (response?.data?.tokenRefresh ?? {});

    if (tokenData.access_token) {
      const userData = yield call(utils.parseToken, tokenData.access_token);

      // сохраняем токен в куку
      yield fork(utils.saveToken, tokenData);
      // сетим данные в стор, если этого не сделать на этом этапе у нас не будет айди юзера
      yield put({ type: USER_ADD_DATA_SUCCESS, userData });

      yield put({
        type: ANALYTICS_SET_USER_PROPERTIES,
        data: {
          role: userData.role,
          registration_type: state.login.registrationType,
        },
      });

      yield put({
        type: ANALYTICS_SEND_EVENT,
        eventName: 'registration',
        data: {
          userRole: userData.role,
          fromPage: window.location.pathname,
          type: state.login.registrationType,
        },
      });

      yield put({
        type: GA_SEND_EVENT,
        eventName: 'regComplete',
        data: {
          userRole: userData.role,
          event: state.login.registrationType === 'email' ? 'regEmail' : 'regGoogle',
        },
      });

      yield fork(regLoginSuccess, tokenData);
    } else {
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true });
      yield put({ type: LOGIN_LOGOUT_REQUEST });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'authUpdateAccess'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  } finally {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: false });
  }
}

export function* googleAuth(gData) {
  try {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: true });

    const gUserData = yield call(utils.convertToken, gData.credential);

    yield authContinue({
      username: gUserData.email,
      password: gData.credential,
      loginType: 'google',
      regType: 'google',
    });
    yield put({
      type: USER_ADD_GOOGLE_DATA_SUCCESS,
      userData: {
        avatarGoogle: gUserData.picture,
        usernameGoogle: gUserData.name,
      },
    });

    yield put({
      type: ANALYTICS_SEND_EVENT,
      eventName: 'login-auth-success',
    });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'googleAuth'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'login', state: false });
    yield put({
      type: ANALYTICS_SEND_EVENT,
      eventName: 'login-auth-error',
      data: {
        errorType: JSON.stringify(e || {}),
      },
    });
  }
}

export function* authRegistration(params: Action & {
  name: string;
  phone: string;
  email: string;
  password: string;
}) {
  const user = yield select((state) => state.user);
  const regData = {
    name: params.name,
    phone: params.phone,
    role: 'student',
    notFree: true,
  };

  // Коли юзер зареєструвався, але при реєстрації студента впала помилка і він реєструється повторно
  if (user?.uid) {
    yield put({
      type: USER_REGISTRATION_PAY_REQUEST,
      data: regData,
      hasValidation: true,
    });
  } else {
    yield fork(registration, params.email, params.password);

    yield take(REGISTRATION_AUTH_SUCCESS);

    yield fork(authContinue, {
      username: params.email,
      password: params.password,
      regType: 'email',
      regData,
    });
  }
}

export function* authRegistrationTeacherPay(params: Action & {
  name: string;
  email: string;
  password: string;
}) {
  yield fork(registrationPay, params.email, params.password);
  yield take(REGISTRATION_PAY_SUCCESS);

  const response = yield call(api.siteAuth, {
    username: params.email,
    password: params.password,
  });
  const tokenData = (response?.data?.tokenAccess ?? {});

  if (tokenData.access_token) {
    const userData = yield call(utils.parseToken, tokenData.access_token);

    yield fork(utils.saveToken, tokenData);
    yield put({
      type: USER_REGISTRATION_REQUEST,
      data: { name: params.name, notFree: true, role: 'teacher' },
    });
    yield put({
      type: ANALYTICS_SET_USER_PROPERTIES,
      data: {
        role: userData.role,
        registration_type: 'email',
      },
    });
    yield put({
      type: ANALYTICS_SEND_EVENT,
      eventName: 'registration',
      data: {
        userRole: userData.role,
        fromPage: window.location.pathname,
        type: 'email',
      },
    });
    yield put({
      type: GA_SEND_EVENT,
      eventName: 'regComplete',
      data: {
        userRole: 'teacher',
        event: 'regEmail',
      },
    });
  } else {
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true });
  }
}

export function* authRegistrationStudentPay(params: Action & {
  email: string;
  password: string;
}) {
  yield fork(registrationPay, params.email, params.password);
  yield take(REGISTRATION_PAY_SUCCESS);

  const response = yield call(api.siteAuth, {
    username: params.email,
    password: params.password,
  });
  const tokenData = (response?.data?.tokenAccess ?? {});

  if (tokenData.access_token) {
    const userData = yield call(utils.parseToken, tokenData.access_token);

    yield fork(utils.saveToken, tokenData);
    yield put({
      type: ANALYTICS_SET_USER_PROPERTIES,
      data: {
        role: userData.role,
        registration_type: 'email',
      },
    });
    yield put({
      type: ANALYTICS_SEND_EVENT,
      eventName: 'registration',
      data: {
        userRole: userData.role,
        fromPage: window.location.pathname,
        type: 'email',
      },
    });
    yield put({
      type: GA_SEND_EVENT,
      eventName: 'regComplete',
      data: {
        userRole: 'teacher',
        event: 'regEmail',
      },
    });
    yield put({
      type: AUTH_SUCCESS,
      isNeedFillInfo: true,
      registrationType: 'email',
    });
  } else {
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true });
  }
}

export function* authAsUser(params: Action & { email: string; }) {
  const response = yield call(userApi.getUsers, params.email);
  const id = response.data?.getUsers?.[0]?.id;

  if (id) {
    const hashResponse = yield call(userApi.getAuthLink, {
      id,
      hash: md5(`${id}_${new Date().getTime()}_allright_pro`),
    });

    yield put({ type: LOGIN_LOGOUT_REQUEST });
    yield delay(2000);
    yield authContinue({
      username: params.email,
      password: hashResponse?.data?.getAuthLink?.hash,
      loginType: 'email',
    });
  }
}

export function* authLogin(params: Action & {
  email: string; password: string; }) {
  yield authContinue({
    username: params.email,
    password: params.password,
    loginType: 'email',
  });
}

export function* authRestore(params: Action & { email: string; hash: string; }) {
  yield authRestoreContinue(params.email, params.hash);
}

export function* logout() {
  yield call(utils.deleteToken, true);
  yield put({
    type: ANALYTICS_SEND_EVENT,
    eventName: 'logout',
  });

  yield put({
    type: REDIRECT_ACTION,
    payload: {
      path: '/login/',
    },
  });
}

export function* resetState() {
  yield put({
    type: LOGIN_RESET_STATE_SUCCESS,
    isNeedFillInfo: false,
  });
}

export function* initGSI() {
  if (
    window.location.pathname.match(/^\/[a-z]{2}\/login\/$/)
    || decodeInviteLink(window.location.pathname)
  ) {
    if (window.google) {
      try {
        yield put({
          type: ANALYTICS_SEND_EVENT,
          eventName: 'login-init-success',
        });
        yield put({ type: LOGIN_INIT_SUCCESS });

        const gData = yield call(api.googleInit);

        yield googleAuth(gData);
      } catch (e) {
        yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'initGSI'] });
        yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });

        yield put({
          type: ANALYTICS_SEND_EVENT,
          eventName: 'login-init-error',
          data: {
            errorType: JSON.stringify(e || {}),
          },
        });
      }
    } else {
      yield delay(50);
      yield call(initGSI);
    }
  }
}

export default function* login() {
  yield takeLatest(AUTH_AS_USER_REQUEST, authAsUser);
  yield takeLatest(LOGIN_AUTH_REQUEST, authLogin);
  yield takeLatest(LOGIN_RESTORE_REQUEST, authRestore);
  yield takeLatest(REGISTRATION_AUTH_REQUEST, authRegistration);
  // событие обновление токена
  yield takeEvery(LOGIN_AUTH_REFRESH_REQUEST, authRefresh);
  // обновление рефреш токена, нужно только после регистрации так как мы не знаем роль пользователя
  yield takeEvery(LOGIN_AUTH_UPDATE_ACCESS, authUpdateAccess);
  yield takeLatest(LOGIN_RESET_STATE_REQUEST, resetState);
  yield takeLatest(LOGIN_LOGOUT_REQUEST, logout);
  yield takeLatest(LOGIN_INIT_REQUEST, initGSI);
  yield takeLatest(REGISTRATION_TEACHER_PAY_REQUEST, authRegistrationTeacherPay);
  yield takeLatest(REGISTRATION_STUDENT_PAY_REQUEST, authRegistrationStudentPay);
}
